// src/pages/exams/OfflineExam/PlayfulTeacher/index.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useClient } from 'jsonapi-react';
import toast from 'react-hot-toast';
import { FiSearch } from 'react-icons/fi';
import { Filter, FilterContent, FilterActions, FilterInput, FilterSelectNative } from '@/components/custom/filter';
import { DashboardContent } from '@/layouts/dashboard';
import { EmptyState } from '@/components/custom/empty-state';
import { Loader } from '@/components/custom/loader';
import { TiaBanner } from '@/components/custom/tia-banner';
import { PlayfulCardButton } from '@/components/custom/playful-card';
import { PrintOptionsModal } from '@/pages/exams/PrintedExam/PrintOptionsModal';
import { JOINHA } from '@/pages/dashboard/home-teacher/teacher-character';
import { OfflineStudentRowTeacher, getStudentStatus } from './OfflineStudentRowTeacher';

const STATUS_FILTER_OPTIONS = [
  { id: 'not_taken', name: 'Ainda não realizada' },
  { id: 'processing', name: 'Processando' },
  { id: 'completed', name: 'Concluído' },
  { id: 'failed', name: 'Erro ao processar' },
  { id: 'online', name: 'Realizado online' }
];

// Mesma integração de dados da tela Default (Default/index.jsx +
// Default/StudentCard.jsx) — able_to_answer_sheet, reports/answer_sheets,
// institutions, courses/simple, classrooms, e o mesmo fluxo real de envio de
// cartão-resposta (presign S3 -> upload -> answer_sheets). O layout é outro,
// mas os dados e as regras são as mesmas — inclusive "Enviar em lote" e
// "Imprimir", que agora reaproveitam as mesmas rotas/telas do Default.
export function OfflineExamPlayfulTeacher({ selectedTab }) {
  const navigate = useNavigate();
  const client = useClient();
  const { id } = useParams();

  const [exam, setExam] = useState(null);
  const [students, setStudents] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [institutions, setInstitutions] = useState([]);
  const [courses, setCourses] = useState([]);
  const [classrooms, setClassrooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPrintOptions, setShowPrintOptions] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedInstitution, setSelectedInstitution] = useState('blank');
  const [selectedCourse, setSelectedCourse] = useState('blank');
  const [selectedClassroom, setSelectedClassroom] = useState('blank');
  const [selectedStatus, setSelectedStatus] = useState('blank');

  const getStudents = async ({ silent = false } = {}) => {
    const { data, error } = await client.fetch(`/users/question_books/${id}/able_to_answer_sheet`);
    if (error) {
      if (!silent) toast.error('Erro ao carregar alunos');
    } else {
      setStudents(data || []);
    }
  };

  const getMetrics = async () => {
    const { data, error } = await client.fetch(`/reports/question_books/${id}/answer_sheets`);
    if (error) {
      toast.error('Erro ao carregar métricas');
    } else {
      setMetrics(data);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [examResponse, institutionsResponse, coursesResponse, classroomsResponse] = await Promise.all([client.fetch(`question_books/${id}`), client.fetch('institutions'), client.fetch('courses/simple'), client.fetch('classrooms')]);

        if (examResponse.error) {
          toast.error('Erro ao carregar dados da prova');
        } else {
          setExam(examResponse.data);
        }

        if (institutionsResponse.error) {
          toast.error('Erro ao buscar escolas');
        } else {
          setInstitutions(institutionsResponse.data || []);
        }

        if (coursesResponse.error) {
          toast.error('Erro ao buscar cursos');
        } else {
          setCourses(coursesResponse.data || []);
        }

        if (classroomsResponse.error) {
          toast.error('Erro ao buscar turmas');
        } else {
          setClassrooms(classroomsResponse.data || []);
        }

        await Promise.all([getStudents(), getMetrics()]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Mesma regra de Default/index.jsx: enquanto houver cartão-resposta em
  // processamento (OCR) no backend, busca os status atualizados em segundo
  // plano (sem loader de tela cheia) até que todos saiam de 'processing'.
  useEffect(() => {
    const hasProcessing = students.some(student => getStudentStatus(student) === 'processing');
    if (!hasProcessing) return;

    const interval = setInterval(() => {
      getStudents({ silent: true });
      getMetrics();
    }, 8000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [students]);

  // Turma dependente do curso escolhido (e da escola, se selecionada),
  // mesma regra do Default (Default/index.jsx#filteredClassrooms).
  const classroomOptions = useMemo(() => {
    if (selectedCourse === 'blank') return [];
    let options = classrooms.filter(classroom => classroom?.course?.id === parseInt(selectedCourse, 10));

    if (selectedInstitution !== 'blank') {
      const institutionClassroomIds = institutions.find(item => item.id === parseInt(selectedInstitution, 10))?.['classroom-ids'] || [];
      options = options.filter(classroom => institutionClassroomIds.includes(classroom.id));
    }

    return options;
  }, [classrooms, selectedCourse, selectedInstitution, institutions]);

  const handleCourseChange = e => {
    setSelectedCourse(e.target.value);
    setSelectedClassroom('blank');
  };

  const filteredStudents = students.filter(student => {
    if (searchTerm && !student.name.toLowerCase().includes(searchTerm.toLowerCase())) return false;

    if (selectedInstitution !== 'blank') {
      const institutionClassroomIds = institutions.find(item => item.id === parseInt(selectedInstitution, 10))?.['classroom-ids'] || [];
      if (!student?.['classroom-ids']?.some(classroomId => institutionClassroomIds.includes(classroomId))) return false;
    }

    if (selectedCourse !== 'blank' && !student?.['course-ids']?.some(courseId => courseId === parseInt(selectedCourse, 10))) return false;

    if (selectedClassroom !== 'blank' && selectedCourse !== 'blank' && !student?.['classroom-ids']?.some(classroomId => classroomId === parseInt(selectedClassroom, 10))) return false;

    if (selectedStatus !== 'blank') {
      const status = getStudentStatus(student);
      if (selectedStatus === 'not_taken' ? status !== null : status !== selectedStatus) return false;
    }

    return true;
  });

  const handleStudentSent = () => {
    getStudents({ silent: true });
    getMetrics();
  };

  const breadcrumbs = {
    title: 'Ferramentas de Avaliação',
    nav: [
      { route: `/avaliacoes/${selectedTab}`, name: selectedTab === 'provas' ? 'Provas' : 'Simulados', isActive: false },
      { route: '#', name: exam?.title || 'Prova offline', isActive: true }
    ]
  };

  const kpiItems = [
    { label: 'Provas realizadas', value: `${metrics?.['question-books-user'] ?? 0}/${metrics?.['student-total'] ?? 0}` },
    { label: 'Provas online', value: metrics?.['question-books-user-without-answer-sheet'] ?? 0 },
    { label: 'Provas offline', value: metrics?.['answer-sheet'] ?? 0 },
    { label: 'Em processamento', value: metrics?.processing ?? 0 },
    { label: 'Erro ao processar', value: metrics?.failed ?? 0 }
  ];

  return (
    <DashboardContent
      variant="plain"
      breadcrumbs={breadcrumbs}
    >
      <TiaBanner
        className="mb-6 sm:mt-24"
        image={JOINHA}
        imageAlt="Personagem do professor"
        imageHeight={215}
        text={exam?.title ? `Organize os cartões-resposta de "${exam.title}"! 📝` : 'Organize os cartões-resposta da prova! 📝'}
      />

      {!loading && (
        <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-5">
          {kpiItems.map(item => (
            <div
              key={item.label}
              data-testid="offline-kpi-tile"
              className="rounded-2xl border border-neutral-100 bg-white p-4 text-center shadow-card"
            >
              <p
                className="offline-kpi-value mb-1 text-2xl font-extrabold"
                style={{ color: '#222731' }}
              >
                {item.value}
              </p>
              <p className="offline-kpi-label text-xs font-semibold text-neutral-500">{item.label}</p>
            </div>
          ))}
        </div>
      )}

      <Filter variant="plain">
        <FilterContent>
          <FilterInput
            label="Buscar"
            endIcon={FiSearch}
            placeholder="Nome do aluno"
            type="search"
            name="offline-search"
            id="offline-search"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
          <FilterSelectNative
            label="Selecione uma escola"
            blankLabel="Todas"
            value={selectedInstitution}
            onChange={e => setSelectedInstitution(e.target.value)}
            options={institutions}
          />
          <FilterSelectNative
            label="Curso"
            blankLabel="Todos"
            value={selectedCourse}
            onChange={handleCourseChange}
            options={courses}
          />
          <FilterSelectNative
            label="Turma"
            blankLabel="Todas"
            value={selectedClassroom}
            onChange={e => setSelectedClassroom(e.target.value)}
            options={classroomOptions}
            disabled={selectedCourse === 'blank'}
          />
          <FilterSelectNative
            label="Status"
            blankLabel="Todos"
            value={selectedStatus}
            onChange={e => setSelectedStatus(e.target.value)}
            options={STATUS_FILTER_OPTIONS}
          />
        </FilterContent>

        <FilterActions>
          <PlayfulCardButton
            variant="outline"
            className="w-auto px-6"
            onClick={() => navigate(`/avaliacoes/${selectedTab}/enviar-lote/${id}`)}
          >
            Enviar cartão-resposta em lote
          </PlayfulCardButton>

          <PlayfulCardButton
            className="w-auto px-6"
            onClick={() => setShowPrintOptions(true)}
          >
            Imprimir {exam?.visibility === 'open' ? 'simulado' : 'prova'}
          </PlayfulCardButton>
        </FilterActions>
      </Filter>

      <PrintOptionsModal
        open={showPrintOptions}
        onOpenChange={setShowPrintOptions}
        onSelectQuestions={() => navigate(`/avaliacoes/${selectedTab}/impressao/${id}`, { state: { mode: 'questions' } })}
        onSelectAnswerSheet={() => navigate(`/avaliacoes/${selectedTab}/impressao/${id}`, { state: { mode: 'answer-sheet', studentIds: filteredStudents.map(student => student.id) } })}
        onSelectFull={() => navigate(`/avaliacoes/${selectedTab}/impressao/${id}`, { state: { mode: 'full', studentIds: filteredStudents.map(student => student.id) } })}
      />

      {loading && <Loader />}

      {!loading && filteredStudents.length === 0 && (
        <EmptyState
          type="content"
          variant="plain"
          className="pt-16 sm:pt-32"
        />
      )}

      {!loading && filteredStudents.length > 0 && (
        <div className="relative flex flex-col gap-3">
          {filteredStudents.map((student, index) => (
            <OfflineStudentRowTeacher
              key={student.id}
              student={student}
              courses={courses}
              classrooms={classrooms}
              examId={id}
              index={index}
              onSent={handleStudentSent}
            />
          ))}
        </div>
      )}
    </DashboardContent>
  );
}
